import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import { optOutText } from 'features/ManageCampaigns/utils/textContentStrings'
import useCopyText from 'hooks/useCopyText'
import { campaignNamesForInternalAdmin } from 'main/campaignNamesAndDesc'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import campaignApi from 'services/api/campaign'

import styles from './CampaignHistory.module.scss'

function getTitle(category) {
  const cleanCategory = category.replace('OTA', '')

  const isOTACampaign = category.match(/OTA/)

  return `History of ${campaignNamesForInternalAdmin[cleanCategory] || category} ${
    isOTACampaign ? 'for OTA guests' : 'for regular guests'
  }`
}

const url = 'on.savibook/hsa88'

function replaceTags(text, defaultData) {
  const name = defaultData.userName.substring(0, defaultData.userName.indexOf(' '))
  const lastName = defaultData.userName.split(' ').pop() || ''
  const hotelName = defaultData.hotelName
  return text
    .replace(/@\[(\S+)\]\((__)?(\S+)(__)?\)/gm, '$1')
    .replace('firstName', name)
    .replace('guestName', name)
    .replace('lastName', lastName)
    .replace('hotelName', hotelName)
    .replace('inviteLink', '|' + url + '|')
    .replace('checkinDate', moment().add(1, 'day').format('MMM D, YYYY'))
    .replace('checkoutDate', moment().add(3, 'day').format('MMM D, YYYY'))
    .replace(optOutText, '')
    .split(/\||/g)
    .map((part, i) =>
      part.includes('on.savibook') ? (
        <span className={styles.bubbleLink} key={`t-${i}`}>
          {part}
        </span>
      ) : (
        <span key={`t-${i}`}>{part}</span>
      )
    )
}

function isEmptyCampaignHistoryError(error) {
  const message =
    error && typeof error.message === 'string'
      ? error.message
      : typeof error === 'string'
      ? error
      : ''

  return message.includes('No contents history defined')
}

const CampaignHistory = ({ onClose, category, contentId, isDefaultCampaigns }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [error, setError] = useState()

  const [activeRows, setActiveRows] = useState([])

  const userName = useSelector(s => s.login.currentUser.name)
  const currentHotel = useSelector(s => s.login.currentHotel)

  const copy = useCopyText()

  const defaultData = {
    userName,
    hotelName: currentHotel
  }

  useEffect(() => {
    setIsLoading(true)
    setIsErred(false)
    setError(undefined)

    campaignApi
      .getCampaignHistory({ contentId, isDefaultCampaigns })
      .then(response => {
        setData(Array.isArray(response) ? response : [])
      })
      .catch(error => {
        if (isEmptyCampaignHistoryError(error)) {
          setData([])
          return
        }

        setError(error)
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [contentId, isDefaultCampaigns])

  if (isErred) {
    return (
      <ConfirmationDialog
        theme="blacklist"
        maxWidth={950}
        title={getTitle(category)}
        onClose={onClose}
      >
        <div
          className={styles.tableHolder}
          style={{ display: 'flex', height: 120, alignItems: 'center' }}
        >
          <p>Something went wrong: {error.toString()} </p>
        </div>
      </ConfirmationDialog>
    )
  }

  if (isLoading) {
    return (
      <ConfirmationDialog
        theme="blacklist"
        maxWidth={950}
        title={getTitle(category)}
        onClose={onClose}
      >
        <div
          className={styles.tableHolder}
          style={{ display: 'flex', height: 120, alignItems: 'center' }}
        >
          <LoadingIndicator />
        </div>
      </ConfirmationDialog>
    )
  }

  if (!data.length) {
    return (
      <ConfirmationDialog
        title={getTitle(category)}
        onClose={onClose}
        theme="blacklist"
        maxWidth={950}
      >
        <div className={styles.tableHolder}>
          <div className={styles.historyEmptyWrap}>
            <div className={styles.historyEmpty}>
              <svg
                className={styles.historyEmptyIcon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 8v5l3 2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 21a9 9 0 1 0-9-9"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M3 12V8h4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className={styles.historyEmptyTitle}>You don't have any history yet.</p>
            </div>
          </div>
        </div>
      </ConfirmationDialog>
    )
  }

  return (
    <ConfirmationDialog
      title={getTitle(category)}
      onClose={onClose}
      theme="blacklist"
      maxWidth={950}
    >
      <div className={styles.tableHolder}>
        <Table widths={[400, 450, 170, 100, 100]}>
          <TitleRow style={{ position: 'sticky', top: 0 }}>
            <TitleCell>Edited by</TitleCell>
            <TitleCell>Edited on</TitleCell>
            <TitleCell>Status</TitleCell>
          </TitleRow>
          {data.map(change => (
            <div className={styles.rowHolder} key={change.historyDate}>
              <div
                className={styles.rowHolderInner}
                onClick={() =>
                  activeRows.includes(change.historyDate)
                    ? setActiveRows([...activeRows.filter(i => i !== change.historyDate)])
                    : setActiveRows([...activeRows, change.historyDate])
                }
              >
                <Row>
                  <Cell className={styles.textCellName}>{change.updatedBy}</Cell>
                  <Cell className={styles.textCell}>
                    {moment(change.updatedOn).format('MMM DD, YYYY hh:mm:ss A')}
                  </Cell>
                  <Cell
                    className={styles.textCell}
                    data-tip={
                      !change.isActive && change.disabledReason
                        ? `"${change.disabledReason}"`
                        : ''
                    }
                    data-for={`history-tooltip-${change.historyDate}`}
                  >
                    {change.isActive ? (
                      <p style={{ fontWeight: '600', color: '#59a95d' }}>Enabled</p>
                    ) : (
                      <p style={{ fontWeight: '600', color: '#ff3b30' }}>Disabled</p>
                    )}
                    {!change.isActive && change.disabledReason && (
                      <ReactTooltip
                        id={`history-tooltip-${change.historyDate}`}
                        place="top"
                        type="dark"
                        effect="solid"
                        className={styles.tooltip}
                      />
                    )}
                  </Cell>
                </Row>
              </div>
              {activeRows.includes(change.historyDate) && (
                <div className={styles.previewHolder}>
                  <div className={styles.preview}>
                    {change.mediaUrl && <div className={styles.textMedia} />}
                    <div className={styles.bubble}>
                      <p>{replaceTags(change.messageTemplate, defaultData)}</p>
                      <span className={styles.bubbleTail}></span>
                    </div>
                  </div>
                  <SecondarySmallButton
                    onClick={() =>
                      copy(
                        change.messageTemplate,
                        `Campaign content for ${moment(change.updatedOn).format(
                          'MMM DD'
                        )}`,
                        false
                      )
                    }
                    className={styles.copyButton}
                  >
                    <CopyIcon />
                    Copy text
                  </SecondarySmallButton>
                </div>
              )}
            </div>
          ))}
        </Table>
      </div>
    </ConfirmationDialog>
  )
}

export default CampaignHistory
